import { AddIcon } from "@minket/brand/icons/add";
import { SmtpIcon } from "@minket/brand/icons/smtp";
import { Badge } from "@minket/ui/components/badge";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense, useState } from "react";
import { stamp } from "@/components/domains/format";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { SubPage } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { Section } from "@/components/page/section";
import { QueryView } from "@/components/page/states";
import { describeError } from "@/lib/api";
import { endpoints, type ResponseOf } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/settings/smtp")({
  component: SmtpSettings,
});

const CreateSmtpDialog = lazy(() =>
  import("@/components/smtp/create.ondemand").then((m) => ({ default: m.CreateSmtpDialog })),
);

const columns = ["Name", "Username", "Region", "Project", "Created, UTC"];
const will = "Each credential will be listed here with its username, region and project.";

type Credential = ResponseOf<typeof endpoints.smtp>["data"][number];

function SmtpSettings() {
  const { organization, projects } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const canManage = useCan("smtp.manage");
  const projectList = projects?.data?.data ?? [];
  const preview = (busy: boolean) => (
    <GhostTable columns={columns} label="SMTP credentials" busy={busy} />
  );
  return (
    <SubPage
      title="SMTP credentials"
      description="Each password is shown once."
      action={
        organization && canManage && projectList.length > 0 ? (
          <Button onClick={() => setCreating(true)}>
            <AddIcon className="size-5" />
            Create credential
          </Button>
        ) : (
          <UnavailableAction
            label="Create credential"
            icon={AddIcon}
            reason={
              canManage ? "Needs a project with a domain" : "Admins and owners create credentials"
            }
          />
        )
      }
    >
      <OrgScope will={will} icon={<SmtpIcon />} headingLevel={3} preview={preview}>
        {(org) => <SmtpList orgId={org.organization_id} canManage={canManage} preview={preview} />}
      </OrgScope>
      <Section
        headingLevel={3}
        title="What SMTP mail skips"
        description="It goes straight to SES, so Minket cannot check it first."
      >
        <ul className="grid max-w-[68ch] list-disc gap-1.5 pl-5 text-sm text-muted-foreground">
          <li>No suppression check, kill switch, daily cap or content checks before it sends.</li>
          <li>
            Still applied: SES suppresses addresses that bounced or complained, the credential sends
            only from the project's domains, and its events reach Emails and webhooks.
          </li>
          <li>Give each client its own credential, so one can be revoked alone.</li>
        </ul>
      </Section>
      {creating && organization ? (
        <Suspense fallback={null}>
          <CreateSmtpDialog
            orgId={organization.organization_id}
            projects={projectList}
            onClose={() => setCreating(false)}
          />
        </Suspense>
      ) : null}
    </SubPage>
  );
}

function SmtpList({
  orgId,
  canManage,
  preview,
}: {
  orgId: string;
  canManage: boolean;
  preview: (busy: boolean) => ReactNode;
}) {
  const query = useQuery(apiQuery(endpoints.smtp, { orgId }));
  const { projects } = useWorkspace();
  const names = new Map((projects?.data?.data ?? []).map((p) => [p.id, p.name]));
  return (
    <QueryView query={query} noun="SMTP credentials" headingLevel={3} preview={preview}>
      {(list) =>
        list.data.length === 0 ? (
          <EmptyState
            data-state="empty"
            icon={<SmtpIcon />}
            headingLevel={3}
            title="No SMTP credentials yet"
            description="Create one for each client that sends over SMTP."
            preview={preview(false)}
          />
        ) : (
          <Table aria-label="SMTP users and their regions" className="min-w-[40rem]">
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column}>{column}</TableHead>
                ))}
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data.map((credential) => (
                <SmtpRow
                  key={credential.id}
                  credential={credential}
                  orgId={orgId}
                  project={names.get(credential.project_id) ?? credential.project_id}
                  canManage={canManage}
                />
              ))}
            </TableBody>
          </Table>
        )
      }
    </QueryView>
  );
}

function SmtpRow({
  credential,
  orgId,
  project,
  canManage,
}: {
  credential: Credential;
  orgId: string;
  project: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const revoke = useMutation({
    mutationFn: () => apiRequest(endpoints.deleteSmtp, { orgId, credentialId: credential.id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.smtp, { orgId }) });
      notify("success", `${credential.name} revoked.`);
    },
    onError: (error) => notify("error", describeError(error).message),
  });
  return (
    <TableRow className="[&>td]:align-middle">
      <TableCell className="font-semibold wrap-break-word">{credential.name}</TableCell>
      <TableCell className="type-data whitespace-nowrap">{credential.username}</TableCell>
      <TableCell className="type-data whitespace-nowrap">{credential.region}</TableCell>
      <TableCell className="wrap-break-word">{project}</TableCell>
      <TableCell className="type-data whitespace-nowrap text-muted-foreground">
        {stamp(credential.created_at)}
      </TableCell>
      <TableCell>
        <span className="flex justify-end gap-1.5">
          {credential.revoked_at ? (
            <Badge variant="secondary">Revoked</Badge>
          ) : canManage && confirming ? (
            <>
              <Button
                variant="destructive"
                size="sm"
                loading={revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                Revoke now
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </>
          ) : canManage ? (
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
              Revoke <span className="sr-only">{credential.name}</span>
            </Button>
          ) : null}
        </span>
      </TableCell>
    </TableRow>
  );
}
